import { AsyncDirective } from 'lit/async-directive.js'
import { directive, type PartInfo, PartType } from 'lit/directive.js'
import { repeat } from 'lit/directives/repeat.js'
import type { ChildPart, TemplateResult } from 'lit/html.js'
import { html, nothing } from 'lit/html.js'
import { ref } from 'lit/directives/ref.js'
import { fromEvent, Subject } from 'rxjs'
import { auditTime, filter, map, takeUntil, tap } from 'rxjs/operators'
import type { AxisGeometry, VirtualizeDirection } from './axis.js'
import { AXIS } from './axis.js'
import type { VirtualizerController } from './controller.js'
import { VirtualizerControllerImpl } from './controller.js'
import { endReachedEvent, rangeEvent, visibilityEvent } from './events.js'

// Writable — narrower than Lit's readonly Ref<T>.
export interface VirtualizerRef {
	value: VirtualizerController | undefined
}

export interface VirtualizeOptions<T> {
	estimateSize?: (index: number) => number
	/** Measure keyed row extents instead of treating estimates as final geometry. */
	measure?: boolean
	/** Keep the first visible keyed row stationary when rows prepend or resize. */
	preserveAnchor?: boolean
	/** Accessory rows can occupy space without defining the reading position. */
	anchorEligible?: (item: T, index: number) => boolean
	/** Keep the trailing edge visible as rows append or resize, until the user scrolls upward. */
	followEnd?: boolean
	overscan?: number
	endReachedThreshold?: number
	ref?: VirtualizerRef
	/** Which axis the scroller windows. Default `'vertical'`. */
	direction?: VirtualizeDirection
}

/**
 * Owns the scroll length alone: one out-of-flow strip as long as the whole
 * list, so the scroller's scroll length is a function of the item count and
 * never of whichever window happens to be rendered. In flow it would have to be
 * split around the window, and every range change would then resize it — moving
 * the very scroll position the next range is measured from.
 *
 * Out of flow is also why it stays compatible with a grid or flex scroller: an
 * absolutely-positioned child is neither a grid item nor a flex item, so it
 * occupies no track and takes no share of the line.
 */
function makeSentinel(totalSize: number, axis: AxisGeometry): HTMLDivElement {
	const el = document.createElement('div')
	el.style.cssText = `position:absolute;top:0;left:0;${axis.cross}:1px;${axis.size}:${totalSize}px;pointer-events:none;`
	el.setAttribute('aria-hidden', 'true')
	return el
}

/**
 * The scrolled-away items before the window, as length — this is what puts the
 * rendered slice at its true offset. It replaces a `transform:translate()` on
 * an empty div, which moved only that div: the items were its SIBLINGS, so the
 * offset reached nothing and every window painted at the start of the scroller.
 *
 * A spacer rather than a wrapper around the items, because a wrapper is one
 * grid item and would collapse the items inside it — they must stay direct
 * children of the scroller for `grid-template-columns:subgrid` on a row to
 * resolve against the scroller's own tracks. The block axis's `1/-1` pin spans
 * every column when the scroller IS a grid and is inert when it is not, as
 * `flex:0 0 auto` is inert off a flex line — and load-bearing on one, where a
 * spacer left at the default `flex-shrink:1` is squeezed to nothing by the
 * overflowing window it is supposed to be offsetting.
 *
 * `overflow-anchor:none` because this box resizes in flow on every range
 * change, and Chrome's scroll anchoring answers an in-flow resize before the
 * viewport by compensating the scroll position to hold the anchored element
 * still — silently moving the position the range was just computed from. That
 * compensation is block-flow-direction only (css-scroll-anchoring-1 §2.2), so
 * on the inline axis the declaration is inert rather than wrong; it stays
 * unconditional because a rail still scrolls its own block axis.
 *
 * `gap` is what the flow parent inserts between the spacer and the first
 * rendered item — a gap the unwindowed list never had there. The negative
 * trailing margin gives it back, so item `first` lands on exactly the offset it
 * would have occupied with every earlier item mounted. At zero the spacer is
 * `display:none` instead: a box that is not laid out gets no gap either side of
 * it, which is what the window at the scroller's own origin needs, and it is
 * also the one composition that is right before the scroller's style has been
 * read at all — a part committed into a detached fragment has no computed style
 * to take a gap from. The node itself stays in the committed array either way,
 * because dropping it would shift lit's positional parts and rebuild the whole
 * window each time the range crosses the first item.
 */
function makeSpacer(size: number, axis: AxisGeometry, gap: number): HTMLDivElement {
	const el = document.createElement('div')
	el.style.cssText =
		size === 0
			? 'display:none;'
			: `${axis.size}:${size}px;flex:0 0 auto;overflow-anchor:none;` +
				(axis.pin ? `${axis.pin}:1/-1;` : '') +
				(gap > 0 ? `${axis.marginEnd}:-${gap}px;` : '')
	el.setAttribute('aria-hidden', 'true')
	return el
}

class VirtualizeDirective<T> extends AsyncDirective {
	private _items: ReadonlyArray<T> = []
	private _keyFn: (item: T, index: number) => unknown = (_item, i) => i
	private _template: (item: T, index: number) => TemplateResult = () => nothing as unknown as TemplateResult
	private _options: VirtualizeOptions<T> = {}

	private _scrollAncestor: Element | null = null
	private _flowParent: Element | null = null
	private _resizeObserver: ResizeObserver | null = null
	private _disconnecting$ = new Subject<void>()
	private _controller = new VirtualizerControllerImpl()
	private _first = 0
	private _last = 0
	private _endReachedAt = -1
	private _snaps = false
	private _gap = 0
	private _sizes = new Map<unknown, number>()
	private _rows = new Map<Element, unknown>()
	private _rowObserver: ResizeObserver | null = null
	private _measurementFrame = 0
	private _atEnd = true
	private _scrollOffset = 0
	private _readingAnchor: { key: unknown; offset: number } | undefined

	constructor(partInfo: PartInfo) {
		super(partInfo)
		if (partInfo.type !== PartType.CHILD) {
			throw new Error('virtualize() must be used in a child binding position.')
		}
	}

	private get _estimateSize(): (index: number) => number {
		return index =>
			(this._options.measure ? this._sizes.get(this._keyFn(this._items[index], index)) : undefined) ??
			this._options.estimateSize?.(index) ??
			48
	}

	private get _overscan(): number {
		return this._options.overscan ?? 5
	}

	private get _endReachedThreshold(): number {
		return this._options.endReachedThreshold ?? 20
	}

	private get _axis(): AxisGeometry {
		return AXIS[this._options.direction ?? 'vertical']
	}

	/**
	 * Resolvable only once the part is actually in the document. On a component's
	 * FIRST render lit commits parts into a detached fragment and appends it to
	 * the shadow root afterwards, so the walk up finds no `parentElement`, falls
	 * back to `document.documentElement`, and — because the result is cached for
	 * the directive's life — binds the scroll listener to a document that never
	 * scrolls. The range then never advances: the list renders its first window,
	 * reports the right scroll length, and simply never pages. That is invisible
	 * to a top-level `render(html, container)` (whose part already has the real
	 * container as its parent) and fires for every in-shadow consumer, which is
	 * why `schmancy-table` could not virtualize.
	 */
	private _isResolvable(part: ChildPart): boolean {
		const parent = part.parentNode instanceof ShadowRoot ? part.parentNode.host : part.parentNode
		// An unassigned light-DOM child can be connected before its disclosure
		// creates a slot. Its shadow descendants have no computed style yet.
		return (
			parent != null && parent.isConnected && (!(parent instanceof Element) || getComputedStyle(parent).overflow !== '')
		)
	}

	private _findScrollAncestor(part: ChildPart): Element {
		let node = part.parentNode
		while (node && node !== document.documentElement) {
			if (node instanceof ShadowRoot) {
				node = node.host
				continue
			}
			if (!(node instanceof Element)) break
			const style = window.getComputedStyle(node)
			const overflow = style[this._axis.overflow] || style.overflow
			if (overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay') {
				return node
			}
			node = node.parentNode
		}
		return document.documentElement
	}

	/**
	 * The two facts about the scroller's own styling the composed window has to
	 * answer to. Re-read whenever the scroller resizes, which is when a media
	 * query that could have flipped either of them would have fired.
	 */
	private _readScrollerStyle(scrollAncestor: Element): void {
		this._snaps = window.getComputedStyle(scrollAncestor).scrollSnapType !== 'none'
		const parent = this._flowParent
		this._gap = parent ? parseFloat(window.getComputedStyle(parent)[this._axis.gap]) || 0 : 0
	}

	private _computeRange(scrollOffset: number, viewportExtent: number): { first: number; last: number } {
		const count = this._items.length
		if (count === 0) return { first: 0, last: 0 }

		const overscan = this._overscan
		let first = count - 1
		let cumulative = 0
		for (let i = 0; i < count; i++) {
			const size = this._estimateSize(i)
			if (cumulative + size > scrollOffset) {
				first = i
				break
			}
			cumulative += size
		}

		let last = first
		let rendered = cumulative
		for (let i = first; i < count; i++) {
			rendered += this._estimateSize(i)
			last = i
			if (rendered >= scrollOffset + viewportExtent) break
		}

		return {
			first: Math.max(0, first - overscan),
			last: Math.min(count - 1, last + overscan),
		}
	}

	private _totalSize(): number {
		let total = 0
		for (let i = 0; i < this._items.length; i++) {
			total += this._estimateSize(i)
		}
		return total
	}

	private _anchor(
		scrollOffset = this._scrollAncestor?.[this._axis.scroll],
	): { key: unknown; offset: number } | undefined {
		if (!this._options.preserveAnchor || scrollOffset === undefined) return
		// Newly mounted rows already occupy their real extent before their first
		// observer delivery; estimates would select a different reading anchor.
		const unmeasured = new Map<unknown, number>()
		if (this._options.measure) {
			for (const [row, key] of this._rows) {
				if (row.isConnected && !this._sizes.has(key)) {
					unmeasured.set(key, row.getBoundingClientRect()[this._axis.size] + this._gap)
				}
			}
		}
		let offset = 0
		for (let i = 0; i < this._items.length; i++) {
			const key = this._keyFn(this._items[i], i)
			const size = unmeasured.get(key) ?? this._estimateSize(i)
			if (offset + size > scrollOffset && (this._options.anchorEligible?.(this._items[i], i) ?? true)) {
				return { key: this._keyFn(this._items[i], i), offset: scrollOffset - offset }
			}
			offset += size
		}
	}

	private _restoreAnchor(anchor: { key: unknown; offset: number } | undefined): number | undefined {
		if (!anchor || !this._scrollAncestor || this._scrollAncestor[this._axis.client] === 0) return
		let offset = 0
		for (let i = 0; i < this._items.length; i++) {
			if (this._keyFn(this._items[i], i) === anchor.key) {
				this._scrollAncestor.scrollTo({
					top: this._axis.scroll === 'scrollTop' ? offset + anchor.offset : this._scrollAncestor.scrollTop,
					left: this._axis.scroll === 'scrollLeft' ? offset + anchor.offset : this._scrollAncestor.scrollLeft,
					behavior: 'instant',
				})
				this._scrollOffset = this._scrollAncestor[this._axis.scroll]
				// The first measurements must retain this key despite estimated prepended rows.
				if (!this._options.followEnd || !this._atEnd) this._readingAnchor = anchor
				return offset + anchor.offset
			}
			offset += this._estimateSize(i)
		}
	}

	private _scrollToEnd(): void {
		const ancestor = this._scrollAncestor
		if (!ancestor || ancestor[this._axis.client] === 0) return
		ancestor.scrollTo({
			top: this._axis.scroll === 'scrollTop' ? ancestor.scrollHeight : ancestor.scrollTop,
			left: this._axis.scroll === 'scrollLeft' ? ancestor.scrollWidth : ancestor.scrollLeft,
			behavior: 'instant',
		})
		this._scrollOffset = ancestor[this._axis.scroll]
	}

	private _observeRow = (element: Element | undefined): void => {
		if (!element) return
		if (!this._rowObserver) {
			this._rowObserver = new ResizeObserver(entries => {
				const anchor = this._readingAnchor ?? this._anchor()
				let changed = false
				for (const entry of entries) {
					const key = this._rows.get(entry.target)
					const size = entry.target.getBoundingClientRect()[this._axis.size] + this._gap
					if (size > 0 && this._sizes.get(key) !== size) {
						this._sizes.set(key, size)
						changed = true
					}
				}
				if (changed && this._options.followEnd && this._atEnd && this._scrollAncestor) {
					if (this._scrollAncestor[this._axis.scroll] >= this._scrollOffset) this._scrollToEnd()
					else this._atEnd = false
				}
				if (changed && this._scrollAncestor && !this._measurementFrame) {
					const scrollOffset = this._scrollAncestor[this._axis.scroll]
					// Write layout outside the observer delivery to avoid resize feedback loops.
					this._measurementFrame = requestAnimationFrame(() => {
						this._measurementFrame = 0
						this._readingAnchor = undefined
						if (!this._scrollAncestor || !this.isConnected) return
						const readingAnchor = this._scrollAncestor[this._axis.scroll] === scrollOffset ? anchor : this._anchor()
						this.setValue(this._buildValue())
						if (this._options.followEnd && this._atEnd) {
							this._scrollToEnd()
						} else this._restoreAnchor(readingAnchor)
						this._onScroll(this._scrollAncestor)
					})
				}
			})
		}
		for (const row of this._rows.keys()) {
			if (!row.isConnected) {
				this._rowObserver.unobserve(row)
				this._rows.delete(row)
			}
		}
		const index = Number(element.getAttribute('data-virtual-row'))
		this._rows.set(element, this._keyFn(this._items[index], index))
		this._rowObserver.observe(element)
	}

	private _installListeners(scrollAncestor: Element): void {
		this._resizeObserver?.disconnect()
		this._controller._bind(scrollAncestor, this._estimateSize, this._axis, this._renderWindowAt)
		this._readScrollerStyle(scrollAncestor)

		// The sentinel is absolutely positioned, so it sizes against the nearest
		// POSITIONED ancestor — on a `static` scroller that is some outer element
		// or the initial containing block, and the scroller then gets no scroll
		// length at all. Promote it once, only when it is static, so a scroller
		// that already positions itself is left alone.
		if (scrollAncestor instanceof HTMLElement) {
			if (getComputedStyle(scrollAncestor).position === 'static') {
				scrollAncestor.style.position = 'relative'
			}
			// Anchoring must be off for the whole scroller, not just the spacer.
			// Excluding the spacer only stops IT being chosen as the anchor; the
			// items remain eligible, and it is their compensation that moves the
			// scroll position out from under the range this directive just computed.
			scrollAncestor.style.overflowAnchor = 'none'
		}

		fromEvent(scrollAncestor, 'scroll')
			.pipe(
				filter(() => scrollAncestor[this._axis.client] > 0),
				map(() => scrollAncestor[this._axis.scroll]),
				// A queued end-scroll event can arrive after a child grows. Its
				// unchanged offset is not a departure from the end.
				filter(offset => offset !== this._scrollOffset),
				tap(offset => {
					const movedBack = offset < this._scrollOffset
					this._readingAnchor = undefined
					this._scrollOffset = offset
					this._atEnd =
						(this._atEnd && !movedBack) ||
						scrollAncestor[this._axis.size === 'height' ? 'scrollHeight' : 'scrollWidth'] -
							scrollAncestor[this._axis.client] -
							scrollAncestor[this._axis.scroll] <=
							1
				}),
				auditTime(16),
				takeUntil(this._disconnecting$),
			)
			.subscribe(() => this._onScroll(scrollAncestor))

		this._resizeObserver = new ResizeObserver(() => {
			this._readScrollerStyle(scrollAncestor)
			this._onScroll(scrollAncestor)
		})
		this._resizeObserver.observe(scrollAncestor)
	}

	private _onScroll(scrollAncestor: Element, commit = true, anchoredOffset?: number): void {
		const axis = this._axis
		const viewportExtent = scrollAncestor[axis.client]
		if (viewportExtent === 0) return
		// Before measurement, DOM scroll extent includes real rows while range
		// lookup uses estimates. Pin both computations to the same size model.
		const scrollOffset =
			this._options.followEnd && this._atEnd
				? Math.max(0, this._totalSize() - viewportExtent)
				: anchoredOffset ??
			(commit
				? scrollAncestor[axis.scroll]
				: Math.min(scrollAncestor[axis.scroll], Math.max(0, this._totalSize() - viewportExtent)))
		const { first, last } = this._computeRange(scrollOffset, viewportExtent)

		const changed = first !== this._first || last !== this._last
		this._first = first
		this._last = last
		this._controller._setRange(first, last)

		if (changed && commit) {
			const edge = this._axis.size === 'height' ? 'top' : 'left'
			const viewport = scrollAncestor.getBoundingClientRect()[edge]
			let readingRow: Element | undefined
			let firstTop = Infinity
			if (this._options.preserveAnchor && !this._atEnd) {
				for (const row of this._rows.keys()) {
					if (!row.isConnected) continue
					const index = Number(row.getAttribute('data-virtual-row'))
					if (this._options.anchorEligible && !this._options.anchorEligible(this._items[index], index)) continue
					const rect = row.getBoundingClientRect()
					if (rect[edge] + rect[this._axis.size] > viewport && rect[edge] < firstTop) {
						readingRow = row
						firstTop = rect[edge]
					}
				}
			}
			const readingTop = readingRow?.getBoundingClientRect()[edge]
			this.setValue(this._buildValue())
			if (readingRow && readingTop !== undefined && readingRow.isConnected) {
				const delta = readingRow.getBoundingClientRect()[edge] - readingTop
				scrollAncestor.scrollTo({
					top: scrollAncestor.scrollTop + (edge === 'top' ? delta : 0),
					left: scrollAncestor.scrollLeft + (edge === 'left' ? delta : 0),
					behavior: 'instant',
				})
				this._scrollOffset = scrollAncestor[this._axis.scroll]
			}
			if (this._options.followEnd && this._atEnd) this._scrollToEnd()
			// Preserve this committed reading position through the first row
			// measurements and card hydration in the same layout cycle.
			else this._readingAnchor = this._anchor()
		}

		const notify = () => {
			if (!this.isConnected || this._scrollAncestor !== scrollAncestor) return
			if (first !== this._first || last !== this._last) return
			if (changed) {
				scrollAncestor.dispatchEvent(rangeEvent(first, last))
				scrollAncestor.dispatchEvent(visibilityEvent(first, last))
			}
			const total = this._items.length
			if (total > 0 && total > this._endReachedAt && last >= total - 1 - this._endReachedThreshold) {
				this._endReachedAt = total
				scrollAncestor.dispatchEvent(endReachedEvent())
			}
		}
		// update() returns its commit to Lit; setValue or notifying consumers
		// before that commit would re-enter a part whose children are still stale.
		if (commit) notify()
		else queueMicrotask(notify)
	}

	/**
	 * Mounts the window covering a scroll offset the scroller has not reached
	 * yet. Mandatory snapping resolves a programmatic scroll against the snap
	 * areas that exist when it is written, so a jump past the rendered window is
	 * clamped back to the nearest mounted item and `scrollToIndex` silently lands
	 * on the wrong one; the destination has to be in the DOM first. Committed
	 * synchronously through `setValue`, so the controller's own scroll assignment
	 * on the next line already sees it.
	 */
	private _renderWindowAt = (scrollOffset: number): void => {
		const scrollAncestor = this._scrollAncestor
		if (!this._snaps || !scrollAncestor) return

		const { first, last } = this._computeRange(Math.max(0, scrollOffset), scrollAncestor[this._axis.client])
		if (first === this._first && last === this._last) return

		this._first = first
		this._last = last
		this._controller._setRange(first, last)
		this.setValue(this._buildValue())
	}

	private _buildValue() {
		const items = this._items
		const first = this._first
		const last = this._last
		const axis = this._axis

		let leadingOffset = 0
		for (let i = 0; i < first; i++) {
			leadingOffset += this._estimateSize(i)
		}
		const windowItems = items.slice(first, last + 1)

		// Items sit BETWEEN the two spacers as direct children of the scroller —
		// never nested in a wrapper — so a grid scroller lays each one out as its
		// own grid item and `grid-template-columns:subgrid` on a row resolves
		// against the scroller's tracks.
		return [
			makeSentinel(this._totalSize(), axis),
			makeSpacer(leadingOffset, axis, this._gap),
			repeat(
				windowItems,
				(item, i) => this._keyFn(item, first + i),
				(item, i) =>
					this._options.measure
						? html`<div data-virtual-row=${first + i} style="display:flow-root;flex:0 0 auto" ${ref(this._observeRow)}>
								${this._template(item, first + i)}
							</div>`
						: this._template(item, first + i),
			),
		]
	}

	private _bindRef(options: VirtualizeOptions<T>): void {
		if (options.ref) {
			options.ref.value = this._controller
		}
	}

	private _clearRef(options: VirtualizeOptions<T>): void {
		if (options.ref) {
			options.ref.value = undefined
		}
	}

	override render(
		_items: ReadonlyArray<T>,
		_keyFn: (item: T, index: number) => unknown,
		_template: (item: T, index: number) => TemplateResult,
		_options?: VirtualizeOptions<T>,
	) {
		return nothing
	}

	override update(
		part: ChildPart,
		[items, keyFn, template, options]: [
			ReadonlyArray<T>,
			(item: T, index: number) => unknown,
			(item: T, index: number) => TemplateResult,
			VirtualizeOptions<T>?,
		],
	) {
		const anchor = this._anchor(this._scrollAncestor?.[this._axis.client] ? undefined : this._scrollOffset)
		this._items = items
		this._keyFn = keyFn
		this._template = template
		this._options = options ?? {}

		if (items.length > this._endReachedAt && this._endReachedAt > 0) {
			this._endReachedAt = -1
		}

		if (!this._scrollAncestor && !this._isResolvable(part)) {
			// Detached first commit — bind nothing yet (a wrong ancestor would be
			// cached for good) and retry once the fragment is in the document.
			// The first window still renders below, so the list paints on frame 1.
			requestAnimationFrame(() => {
				if (!this.isConnected || this._scrollAncestor || this._resizeObserver) return
				const bind = () => {
					if (!this.isConnected || this._scrollAncestor || !this._isResolvable(part)) return
					const ancestor = this._findScrollAncestor(part)
					this._scrollAncestor = ancestor
					this._flowParent = part.parentNode instanceof Element ? part.parentNode : null
					this._installListeners(ancestor)
					this._bindRef(this._options)
					if (this._options.followEnd && this._atEnd) {
						this._onScroll(ancestor, true, Math.max(0, this._totalSize() - ancestor[this._axis.client]))
						this._scrollToEnd()
					}
					this._onScroll(ancestor)
				}
				// Slot insertion gives the waiting viewport its first layout box.
				// Observe that lifecycle edge instead of polling hidden content.
				this._resizeObserver = new ResizeObserver(() => requestAnimationFrame(bind))
				if (part.parentNode instanceof Element) this._resizeObserver.observe(part.parentNode)
				else if (part.parentNode instanceof ShadowRoot) this._resizeObserver.observe(part.parentNode.host)
				bind()
			})
		} else if (!this._scrollAncestor) {
			const ancestor = this._findScrollAncestor(part)
			this._scrollAncestor = ancestor
			this._flowParent = part.parentNode instanceof Element ? part.parentNode : null
			this._installListeners(ancestor)
			this._bindRef(this._options)

			const { first, last } = this._computeRange(
				this._options.followEnd && this._atEnd
					? Math.max(0, this._totalSize() - ancestor[this._axis.client])
					: this._options.preserveAnchor
						? this._scrollOffset
						: ancestor[this._axis.scroll],
				ancestor[this._axis.client],
			)
			this._first = first
			this._last = last
			this._controller._setRange(first, last)
		} else {
			this._bindRef(this._options)
			this._controller._bind(this._scrollAncestor, this._estimateSize, this._axis, this._renderWindowAt)
			this._onScroll(this._scrollAncestor, false, this._restoreAnchor(anchor))
		}

		if (anchor || (this._options.followEnd && this._atEnd))
			queueMicrotask(() => {
				if (this._scrollAncestor && this._options.followEnd && this._atEnd) {
					this._scrollToEnd()
				} else this._restoreAnchor(anchor)
				if (this._scrollAncestor) this._onScroll(this._scrollAncestor)
			})
		return this._buildValue()
	}

	override disconnected(): void {
		this._disconnecting$.next()
		this._disconnecting$.complete()
		this._resizeObserver?.disconnect()
		this._rowObserver?.disconnect()
		cancelAnimationFrame(this._measurementFrame)
		this._measurementFrame = 0
		this._readingAnchor = undefined
		this._rowObserver = null
		this._rows.clear()
		this._resizeObserver = null
		this._clearRef(this._options)
		this._scrollAncestor = null
		this._flowParent = null
		this._snaps = false
		this._gap = 0
	}

	override reconnected(): void {
		this._disconnecting$ = new Subject<void>()
		this._scrollAncestor = null
		this._flowParent = null
	}
}

export const virtualize = directive(VirtualizeDirective) as <T>(
	items: ReadonlyArray<T>,
	keyFn: (item: T, index: number) => unknown,
	template: (item: T, index: number) => TemplateResult,
	options?: VirtualizeOptions<T>,
) => ReturnType<typeof directive>

export { VirtualizeDirective }
